import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Sparkles, Clock, Flower, FlaskConical, Moon, Plus, Minus, Check } from 'lucide-react';
import { toast } from 'sonner';
import { BULK_TIERS, getBulkDiscount, getDiscountedPrice, getSavings } from '../utils/pricing';
import { trackViewItem } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderType, setOrderType] = useState('stems');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        trackViewItem(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const getUnitPrice = () => {
    if (!product) return 0;
    switch (orderType) {
      case 'stems':
        return getDiscountedPrice(product.pricePerStem, quantity);
      case 'subscription-weekly':
        return product.subscriptionWeekly;
      case 'subscription-monthly':
        return product.subscriptionMonthly;
      default:
        return product.pricePerStem;
    }
  };

  const getTotalPrice = () => {
    return getUnitPrice() * quantity;
  };

  const getOrderTypeLabel = () => {
    switch (orderType) {
      case 'stems':
        return `${quantity} ${quantity === 1 ? 'Stem' : 'Stems'}`;
      case 'subscription-weekly':
        return 'Weekly Subscription';
      case 'subscription-monthly':
        return 'Monthly Subscription';
      default:
        return `${quantity} Stems`;
    }
  };

  const handleAddToCart = () => {
    addToCart(product, orderType, quantity);
    toast.success('Added to cart', {
      description: `${product.name} · ${getOrderTypeLabel()}`,
      icon: '✿',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <p className="text-brand-green-dark font-body">Loading...</p>
      </div>
    );
  }

  if (!product) return null;

  const discount = orderType === 'stems' ? getBulkDiscount(quantity) : 0;
  const savings = orderType === 'stems' ? getSavings(product.pricePerStem, quantity) : 0;

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 fade-in" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="lg:sticky lg:top-24 h-fit" data-testid="product-image">
            <div className="aspect-[3/4] gold-border-glow overflow-hidden bg-brand-blush/20">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div data-testid="product-info">
            <div className="flex items-center gap-2 mb-4">
              <span className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold">
                A-Grade Quality
              </span>
              <span className="text-brand-gold/50">•</span>
              <span className="text-brand-green-dark/60 text-xs font-body">
                {product.color}
              </span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-brand-green-dark mb-2">
              {product.name}
            </h1>
            <p className="font-body text-base text-brand-green-dark/60 italic mb-6">
              {product.variety}
            </p>

            <p className="font-body text-base leading-relaxed font-light text-brand-green-dark mb-8">
              {product.description}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-2 text-brand-green-dark">
                <Sparkles className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">A-Grade Quality</span>
              </div>
              <div className="flex items-center gap-2 text-brand-green-dark">
                <Clock className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">2-Day Delivery</span>
              </div>
              <div className="flex items-center gap-2 text-brand-green-dark">
                <Flower className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">Vase Ready</span>
              </div>
              <div className="flex items-center gap-2 text-brand-green-dark">
                <FlaskConical className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">Beautiful Aroma</span>
              </div>
              <div className="flex items-center gap-2 text-brand-green-dark col-span-2">
                <Moon className="w-5 h-5 text-brand-gold" strokeWidth={1.5} />
                <span className="font-body text-sm">Ready for a beautiful evening</span>
              </div>
            </div>

            <div className="border-t border-brand-gold/30 pt-8 mb-8">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
                Select Order Type
              </p>

              <div className="space-y-3 mb-8" data-testid="order-type-selector">
                <button
                  onClick={() => setOrderType('stems')}
                  className={`w-full p-4 rounded-sm border smooth-transition text-left flex justify-between items-center ${
                    orderType === 'stems'
                      ? 'bg-brand-green text-brand-cream border-brand-green'
                      : 'bg-brand-cream text-brand-green-dark border-brand-gold/30 hover:border-brand-gold'
                  }`}
                  data-testid="order-type-stems"
                >
                  <div>
                    <p className="font-body text-sm font-bold mb-1">Buy Stems</p>
                    <p className="text-xs opacity-80">Automatic bulk discount at 10+ stems</p>
                  </div>
                  <p className="text-sm font-body">₹{product.pricePerStem}/stem</p>
                </button>

                <button
                  onClick={() => setOrderType('subscription-weekly')}
                  className={`w-full p-4 rounded-sm border smooth-transition text-left flex justify-between items-center ${
                    orderType === 'subscription-weekly'
                      ? 'bg-brand-green text-brand-cream border-brand-green'
                      : 'bg-brand-cream text-brand-green-dark border-brand-gold/30 hover:border-brand-gold'
                  }`}
                  data-testid="order-type-subscription-weekly"
                >
                  <div>
                    <p className="font-body text-sm font-bold mb-1">Weekly Subscription</p>
                    <p className="text-xs opacity-80">5 fresh stems delivered every week</p>
                  </div>
                  <p className="text-sm font-body">₹{product.subscriptionWeekly}/week</p>
                </button>

                <button
                  onClick={() => setOrderType('subscription-monthly')}
                  className={`w-full p-4 rounded-sm border smooth-transition text-left flex justify-between items-center ${
                    orderType === 'subscription-monthly'
                      ? 'bg-brand-green text-brand-cream border-brand-green'
                      : 'bg-brand-cream text-brand-green-dark border-brand-gold/30 hover:border-brand-gold'
                  }`}
                  data-testid="order-type-subscription-monthly"
                >
                  <div>
                    <p className="font-body text-sm font-bold mb-1">Monthly Subscription</p>
                    <p className="text-xs opacity-80">20 fresh stems delivered every month</p>
                  </div>
                  <p className="text-sm font-body">₹{product.subscriptionMonthly}/month</p>
                </button>
              </div>

              {/* Bulk pricing tiers - show only for stems */}
              {orderType === 'stems' && (
                <div className="bg-brand-sage/20 p-6 rounded-sm mb-8 border border-brand-gold/30" data-testid="bulk-tiers-display">
                  <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
                    Bulk Discount Tiers
                  </p>
                  <div className="space-y-3">
                    {BULK_TIERS.map((tier, idx) => {
                      const isActive = quantity >= tier.minQuantity && (tier.maxQuantity === null || quantity <= tier.maxQuantity);
                      return (
                        <div
                          key={`tier-${tier.discount}`}
                          className={`flex justify-between items-center py-2 px-3 rounded-sm smooth-transition ${
                            isActive ? 'bg-brand-green text-brand-cream' : ''
                          }`}
                          data-testid={`tier-${tier.discount}`}
                        >
                          <div className="flex items-center gap-2">
                            {isActive && <Check className="w-4 h-4" strokeWidth={2} />}
                            <span className={`font-body text-sm ${isActive ? '' : 'text-brand-green-dark'}`}>
                              {tier.label}
                            </span>
                          </div>
                          <span className={`font-body text-sm font-bold ${isActive ? '' : 'text-brand-gold'}`}>
                            {tier.discount}% off
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mb-8">
                <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
                  Quantity
                </p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-cream smooth-transition rounded-sm"
                    data-testid="quantity-decrease"
                  >
                    <Minus className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <span className="text-brand-green-dark font-body text-xl w-12 text-center" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center border border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-brand-cream smooth-transition rounded-sm"
                    data-testid="quantity-increase"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div className="bg-brand-blush/30 p-6 gold-border-glow mb-8 rounded-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-body text-brand-green-dark/80 text-sm">Order Type:</span>
                  <span className="font-body text-brand-green-dark">{getOrderTypeLabel()}</span>
                </div>
                {orderType === 'stems' && (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-body text-brand-green-dark/80 text-sm">Price per stem:</span>
                      <span className="font-body text-brand-green-dark">
                        {discount > 0 && (
                          <span className="line-through text-brand-green-dark/40 mr-2">₹{product.pricePerStem}</span>
                        )}
                        ₹{getUnitPrice()}
                      </span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-body text-brand-gold text-sm">Bulk Discount:</span>
                        <span className="font-body text-brand-gold font-bold">
                          {discount}% off · Save ₹{savings}
                        </span>
                      </div>
                    )}
                  </>
                )}
                <div className="border-t border-brand-gold/30 pt-4 mt-4 flex justify-between items-center">
                  <span className="font-body text-brand-gold uppercase tracking-[0.2em] text-sm font-bold">
                    Total
                  </span>
                  <span className="font-heading text-3xl text-brand-green-dark" data-testid="product-total-price">
                    ₹{getTotalPrice()}
                  </span>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                className="w-full bg-brand-green text-brand-cream py-5 sm:py-6 px-8 min-h-[64px] rounded-sm font-body uppercase tracking-[0.2em] text-base sm:text-lg font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition shadow-md hover:shadow-lg active:scale-[0.99]"
                data-testid="add-to-cart-btn"
              >
                Add to Cart · ₹{getTotalPrice()}
              </button>
            </div>

            <div className="border-t border-brand-gold/30 pt-8">
              <p className="font-body text-sm text-brand-green-dark/70 leading-relaxed">
                <strong className="text-brand-green">Delivery:</strong> Available across Hyderabad. Orders placed before 2 PM are delivered within 2 business days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
